<template>
  <!-- 流程管理：把多个 Step 编排成顺序链路，失败时按节点策略中断。 -->
  <section class="view flows-view">
    <div class="split-grid compact">
      <section class="surface">
        <div class="surface-head">
          <div>
            <h2>流程列表</h2>
            <span>{{ flows.length }} 条流程</span>
          </div>
        </div>

        <el-table
          :data="flows"
          row-key="id"
          class="flow-table"
          height="420"
          highlight-current-row
          :row-class-name="flowRowClassName"
          @row-click="selectFlowByRow"
        >
          <el-table-column label="流程" min-width="220">
            <template #default="{ row }">
              <div class="job-name">
                <strong>#{{ row.id }} {{ row.name }}</strong>
                <span>{{ flowChainLabel(row) }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="节点数" width="90" align="center">
            <template #default="{ row }">{{ row.steps ? row.steps.length : 0 }}</template>
          </el-table-column>
          <el-table-column label="结果" width="120">
            <template #default="{ row }">
              <el-tag v-if="row.last_result" :type="row.last_result.ok ? 'success' : 'danger'" effect="plain">
                {{ row.last_result.ok ? '通过' : '失败' }}
              </el-tag>
              <span v-else class="muted">未执行</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="284" fixed="right">
            <template #default="{ row }">
              <el-button
                size="small"
                :icon="icons.VideoPlay"
                :loading="runningFlowId === Number(row.id)"
                @click.stop="runFlow(row.id)"
              >
                执行
              </el-button>
              <el-button
                size="small"
                :icon="icons.Odometer"
                :loading="runningFlowStressId === Number(row.id)"
                @click.stop="openFlowStressDialog(row)"
              >
                压测
              </el-button>
              <el-button
                size="small"
                :icon="icons.Download"
                :loading="exportingFlowReportId === Number(row.id)"
                :disabled="!row.last_result"
                @click.stop="exportFlowReport(row.id)"
              >
                导出报告
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section class="surface editor-surface">
        <div class="surface-head">
          <div>
            <h2>{{ flowForm.id ? `编辑流程 #${flowForm.id}` : '新增流程' }}</h2>
            <span>{{ selectedFlowJobs.length }} 个节点</span>
          </div>
          <el-button
            v-if="flowForm.id"
            type="danger"
            plain
            :icon="icons.Delete"
            @click="deleteFlow"
          >
            删除
          </el-button>
        </div>

        <el-form
          ref="flowEditor"
          :model="flowForm"
          :rules="flowFormRules"
          label-position="top"
          class="flow-form"
          @submit.prevent
        >
          <div class="form-section">
            <div class="section-title">
              <h3>Step 节点列表</h3>
              <span>{{ selectedFlowJobs.length }} 个节点</span>
            </div>
            <div v-if="selectedFlowJobs.length" class="flow-session-fill-control">
              <el-input
                v-model="flowSessionPhone"
                clearable
                placeholder="手机号"
                inputmode="tel"
              />
              <el-button
                :icon="icons.Key"
                :loading="fillingFlowSession"
                :disabled="!flowSessionTargetJobs.length"
                @click="fillSessionForFlow"
              >
                填充全部 session/userId
              </el-button>
              <span class="muted">将填充 {{ flowSessionTargetJobs.length }} 个业务接口</span>
            </div>
            <div v-if="selectedFlowJobs.length" class="flow-node-list">
              <div
                v-for="(job, index) in selectedFlowJobs"
                :key="flowNodeKey(job, index)"
                class="flow-node-row"
                :class="{ missing: job.missing, dragging: draggedFlowIndex === index }"
                @dragover.prevent
                @drop.prevent="handleFlowNodeDrop(index)"
              >
                <el-button
                  class="flow-drag-handle"
                  text
                  :icon="icons.Rank"
                  draggable="true"
                  title="拖拽移动"
                  @dragstart="handleFlowNodeDragStart(index, $event)"
                  @dragend="handleFlowNodeDragEnd"
                />
                <span class="flow-node-index">{{ index + 1 }}</span>
                <div class="flow-node-main">
                  <div class="flow-node-title">
                    <strong>{{ flowNodeLabel(job) }}</strong>
                    <el-tag
                      v-if="!job.missing"
                      size="small"
                      effect="plain"
                      :type="methodTagType(job.http_type)"
                    >
                      {{ job.http_type || 'GET' }}
                    </el-tag>
                  </div>
                  <span class="flow-node-url">{{ job.url || 'Step 已不存在' }}</span>
                </div>
                <div class="flow-node-actions">
                  <el-button
                    size="small"
                    :icon="icons.ArrowUp"
                    :disabled="index === 0"
                    title="上移"
                    @click="moveFlowStep(index, -1)"
                  />
                  <el-button
                    size="small"
                    :icon="icons.ArrowDown"
                    :disabled="index === selectedFlowJobs.length - 1"
                    title="下移"
                    @click="moveFlowStep(index, 1)"
                  />
                  <el-button
                    v-if="canFillSession(job)"
                    size="small"
                    :icon="icons.Key"
                    :loading="fillSessionJobId === Number(job.id)"
                    @click="fillSessionForJob(job)"
                  >
                    填充 session/userId
                  </el-button>
                  <el-button
                    size="small"
                    type="danger"
                    plain
                    :icon="icons.Delete"
                    title="删除节点"
                    @click="removeFlowStep(index)"
                  />
                </div>
              </div>
            </div>
            <div v-else class="flow-empty-chain">暂无绑定 Step</div>
          </div>

          <div class="form-section">
            <div class="section-title">
              <h3>基本信息</h3>
            </div>
            <el-form-item label="流程名称" prop="name">
              <el-input
                :model-value="flowForm.name"
                maxlength="120"
                show-word-limit
                @update:model-value="updateFlowField('name', $event)"
              />
            </el-form-item>
            <el-form-item label="流程描述">
              <el-input
                :model-value="flowForm.description"
                type="textarea"
                :autosize="{ minRows: 2, maxRows: 4 }"
                @update:model-value="updateFlowField('description', $event)"
              />
            </el-form-item>
          </div>

          <div class="form-section">
            <div class="section-title">
              <h3>添加节点</h3>
              <span v-if="flowForm.bindSteps && flowForm.bindSteps.length">
                已选 {{ flowForm.bindSteps.length }} 个
              </span>
            </div>
            <div class="flow-add-group">
              <div class="flow-add-head">
                <strong>登录链路</strong>
                <span v-if="loginCredentials">userId {{ loginCredentials.userId }}</span>
              </div>
              <div v-if="authJobs.length" class="flow-auth-jobs">
                <el-button
                  v-for="job in authJobs"
                  :key="job.step"
                  class="flow-auth-button"
                  :class="{ selected: isBindStepSelected(job.step) }"
                  :icon="icons.Plus"
                  @click="toggleBindStep(job.step)"
                >
                  {{ flowNodeLabel(job) }}
                </el-button>
              </div>
              <span v-else class="muted">没有登录/验证码 Step</span>
            </div>
            <div class="flow-bind-grid">
              <el-form-item label="其他接口">
                <el-select
                  :model-value="selectedBusinessBindIds"
                  clearable
                  filterable
                  multiple
                  collapse-tags
                  collapse-tags-tooltip
                  placeholder="选择其他接口"
                  @update:model-value="updateBusinessBindSteps"
                >
                  <el-option
                    v-for="job in businessJobs"
                    :key="job.step"
                    :label="flowNodeLabel(job)"
                    :value="String(job.step)"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="插入位置">
                <el-select
                  :model-value="flowForm.afterStep"
                  clearable
                  filterable
                  placeholder="追加到末尾"
                  @update:model-value="updateFlowField('afterStep', $event)"
                >
                  <el-option
                    v-for="job in flowInsertAfterJobs"
                    :key="flowNodeKey(job)"
                    :label="flowJobOptionLabel(job)"
                    :value="String(job.step)"
                  />
                </el-select>
              </el-form-item>
            </div>
          </div>

          <div class="form-actions flow-actions">
            <el-button type="primary" :icon="icons.Select" @click="handleSaveFlow">
              保存流程
            </el-button>
            <el-button
              :icon="icons.Plus"
              :disabled="!flowForm.bindSteps || !flowForm.bindSteps.length"
              @click="bindFlowJob"
            >
              添加 Step
            </el-button>
            <el-button
              v-if="flowForm.id"
              :icon="icons.VideoPlay"
              :loading="runningFlowId === Number(flowForm.id)"
              @click="runFlow(Number(flowForm.id))"
            >
              执行流程
            </el-button>
            <el-button
              v-if="flowForm.id"
              :icon="icons.Odometer"
              :loading="runningFlowStressId === Number(flowForm.id)"
              @click="openFlowStressDialog(selectedFlow || flowForm)"
            >
              压力测试
            </el-button>
            <el-button
              v-if="flowForm.id"
              :icon="icons.Download"
              :loading="exportingFlowReportId === Number(flowForm.id)"
              :disabled="!selectedFlow || !selectedFlow.last_result"
              @click="exportFlowReport(Number(flowForm.id))"
            >
              导出测试报告
            </el-button>
          </div>
        </el-form>
      </section>
    </div>

    <section class="surface">
      <div class="surface-head">
        <div>
          <h2>流程执行结果</h2>
          <span v-if="selectedFlow">流程 #{{ selectedFlow.id }}</span>
          <span v-else>暂无选择</span>
        </div>
      </div>
      <section v-if="currentFlowFailures.length" class="assertion-failures">
        <h3>断言失败明细</h3>
        <ul class="assertion-failure-list">
          <li v-for="reason in currentFlowFailures" :key="reason">{{ reason }}</li>
        </ul>
      </section>
      <div v-if="currentFlowSteps.length" class="flow-result-list">
        <section
          v-for="(step, index) in currentFlowSteps"
          :key="flowResultStepKey(step, index)"
          class="flow-result-node"
        >
          <div class="flow-result-node-head">
            <div>
              <h3>{{ flowResultStepLabel(step) }}</h3>
              <span>{{ flowResultUrl(step) }}</span>
            </div>
            <div class="flow-result-tags">
              <el-tag :type="step.ok ? 'success' : 'danger'" effect="plain">
                {{ step.ok ? '节点通过' : '节点失败' }}
              </el-tag>
              <span class="flow-interrupt-muted">
                {{ flowResultBlockFlow(step) ? '失败打断流程' : '失败不打断流程' }}
              </span>
            </div>
          </div>

          <div class="result-grid flow-step-result-grid">
            <section class="result-panel">
              <h3>请求</h3>
              <pre class="response-box">{{ flowResultRequestText(step) }}</pre>
            </section>
            <section class="result-panel">
              <h3>响应</h3>
              <pre class="response-box">{{ flowResultResponseText(step) }}</pre>
            </section>
          </div>

          <section class="flow-rule-result-section">
            <div class="flow-rule-section-head">
              <h3>Rule</h3>
              <span>{{ flowResultRules(step).length }} 条</span>
            </div>
            <ul v-if="flowResultRules(step).length" class="flow-rule-result-list">
              <li
                v-for="(rule, ruleIndex) in flowResultRules(step)"
                :key="`${flowResultStepKey(step, index)}-${ruleIndex}`"
                class="flow-rule-result-row"
              >
                <div class="flow-rule-result-main">
                  <strong>{{ rule.label }}</strong>
                  <span v-if="rule.detail">{{ rule.detail }}</span>
                </div>
                <div class="flow-rule-result-tags">
                  <el-tag :type="rule.ok ? 'success' : 'danger'" size="small" effect="plain">
                    {{ rule.ok ? '通过' : '失败' }}
                  </el-tag>
                  <span class="flow-interrupt-muted small">
                    {{ rule.blockFlow ? '失败打断' : '失败不打断' }}
                  </span>
                </div>
              </li>
            </ul>
            <div v-else class="flow-rule-empty">暂无 Rule</div>
          </section>
        </section>
      </div>
      <div v-else class="flow-empty-chain">暂无执行结果</div>
    </section>

    <el-dialog
      v-model="stressDialogVisible"
      title="流程压力测试"
      width="760px"
      class="stress-dialog"
    >
      <section class="stress-target">
        <strong>{{ stressFlowLabel }}</strong>
        <span>{{ stressTargetFlow && stressTargetFlow.steps ? stressTargetFlow.steps.length : 0 }} 个节点</span>
      </section>

      <el-form label-position="top" class="stress-form">
        <div class="stress-form-grid">
          <el-form-item label="线程数">
            <el-input-number v-model="stressForm.threads" :min="1" :max="20" :step="1" />
          </el-form-item>
          <el-form-item label="轮次数量">
            <el-input-number v-model="stressForm.rounds" :min="1" :max="stressMaxRounds" :step="1" />
          </el-form-item>
          <el-form-item label="Rule">
            <el-switch
              v-model="stressForm.includeRules"
              active-text="包含 Rule"
              inactive-text="不带 Rule"
            />
          </el-form-item>
        </div>
      </el-form>

      <section v-if="stressTask" class="stress-progress">
        <div class="stress-progress-head">
          <span>{{ stressTaskStatusText }}</span>
          <strong>{{ stressTaskCompleted }} / {{ stressTaskTotal }}</strong>
        </div>
        <el-progress :percentage="stressProgressPercent" :status="stressProgressStatus" />
      </section>

      <section v-if="stressResult" class="stress-result">
        <div class="stress-metric-grid">
          <div class="stress-metric">
            <span>成功率</span>
            <strong>{{ stressResult.success_rate }}%</strong>
          </div>
          <div class="stress-metric">
            <span>成功 / 失败</span>
            <strong>{{ stressResult.success_count }} / {{ stressResult.failed_count }}</strong>
          </div>
          <div class="stress-metric">
            <span>QPS</span>
            <strong>{{ stressResult.throughput_per_second }}</strong>
          </div>
          <div class="stress-metric">
            <span>平均耗时</span>
            <strong>{{ formatStressMs(stressResult.avg_ms) }}</strong>
          </div>
          <div class="stress-metric">
            <span>P95</span>
            <strong>{{ formatStressMs(stressResult.p95_ms) }}</strong>
          </div>
          <div class="stress-metric">
            <span>总耗时</span>
            <strong>{{ formatStressMs(stressResult.total_duration_ms) }}</strong>
          </div>
        </div>
        <ul v-if="(stressResult.failed_samples || []).length" class="stress-failure-list">
          <li v-for="sample in stressResult.failed_samples || []" :key="sample.index">
            #{{ sample.index }} {{ formatStressMs(sample.duration_ms) }}：{{ sample.failure }}
          </li>
        </ul>
      </section>

      <template #footer>
        <el-button @click="stressDialogVisible = false">关闭</el-button>
        <el-button
          type="primary"
          :loading="Boolean(stressTargetFlow && runningFlowStressId === Number(stressTargetFlow.id))"
          :disabled="stressRunning"
          @click="handleRunFlowStress"
        >
          {{ stressRunning ? "压测中" : "开始压测" }}
        </el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { ArrowDown, ArrowUp, Delete, Download, Key, Odometer, Plus, Rank, Select, VideoPlay } from "@element-plus/icons-vue";
import {
  flowChainLabel,
  flowJobOptionLabel,
  jobOptionLabel,
  methodTagType,
} from "../utils/job-display";
import {
  assertionFailureReasons,
  formatRequestParams,
  formatResponseBody,
} from "../utils/result-format";
import {
  extractLoginCredentials,
  isAuthJob,
  isLoginJob,
  isVerifyCodeJob,
} from "../utils/session-headers";
import {
  dropFlowStepAt,
  moveFlowStepAt,
  removeFlowStepAt,
} from "../store/form-helpers";

function orderedStep(job) {
  const step = Number(job && job.step);
  return Number.isFinite(step) ? step : Number.MAX_SAFE_INTEGER;
}

export default {
  name: "FlowsPage",

  data() {
    return {
      icons: {
        ArrowDown,
        ArrowUp,
        Delete,
        Download,
        Key,
        Odometer,
        Plus,
        Rank,
        Select,
        VideoPlay,
      },
      draggedFlowIndex: null,
      fillSessionJobId: null,
      fillingFlowSession: false,
      flowSessionPhone: "",
      stressDialogVisible: false,
      stressTargetFlow: null,
      stressTask: null,
      stressPollTimer: null,
      stressResult: null,
      stressForm: {
        threads: 1,
        rounds: 10,
        includeRules: true,
      },
      flowFormRules: {
        name: [{ required: true, message: "请输入流程名称", trigger: "blur" }],
      },
    };
  },

  computed: {
    ...mapState([
      "jobs",
      "flows",
      "selectedFlow",
      "runningFlowId",
      "runningFlowStressId",
      "exportingFlowReportId",
      "flowForm",
    ]),
    ...mapGetters(["currentFlowResult"]),

    selectedFlowJobs() {
      const fallbackJobs = new Map(
        (this.selectedFlow && Array.isArray(this.selectedFlow.jobs) ? this.selectedFlow.jobs : []).map((job) => [
          String(job.step || ""),
          job,
        ]),
      );
      return (Array.isArray(this.flowForm.steps) ? this.flowForm.steps : []).map((step) => {
        const normalizedStep = String(step || "").trim();
        return (
          this.jobs.find((job) => String(job.step || "").trim() === normalizedStep) ||
          fallbackJobs.get(normalizedStep) || { step: normalizedStep, missing: true }
        );
      });
    },

    orderedJobs() {
      return [...this.jobs].sort((left, right) => {
        const stepDiff = orderedStep(left) - orderedStep(right);
        if (stepDiff !== 0) {
          return stepDiff;
        }
        return Number(left.id) - Number(right.id);
      });
    },

    authJobs() {
      return this.orderedJobs.filter((job) => isAuthJob(job));
    },

    businessJobs() {
      return this.orderedJobs.filter((job) => !isAuthJob(job));
    },

    selectedBusinessBindIds() {
      const businessSteps = new Set(this.businessJobs.map((job) => String(job.step || "").trim()));
      return (this.flowForm.bindSteps || [])
        .map((step) => String(step || "").trim())
        .filter((step) => businessSteps.has(step));
    },

    loginJob() {
      return (
        this.selectedFlowJobs.find((job) => isLoginJob(job)) ||
        this.authJobs.find((job) => isLoginJob(job)) ||
        null
      );
    },

    verifyCodeJob() {
      return (
        this.selectedFlowJobs.find((job) => isVerifyCodeJob(job) && !isLoginJob(job)) ||
        this.authJobs.find((job) => isVerifyCodeJob(job) && !isLoginJob(job)) ||
        this.authJobs.find((job) => isVerifyCodeJob(job)) ||
        null
      );
    },

    loginCredentials() {
      return extractLoginCredentials(this.loginJob);
    },

    flowSessionTargetJobs() {
      return this.selectedFlowJobs.filter((job) => this.canFillSession(job));
    },

    flowInsertAfterJobs() {
      const adding = new Set((this.flowForm.bindSteps || []).map((step) => String(step || "").trim()));
      return this.selectedFlowJobs.filter((job) => !adding.has(String(job.step || "").trim()));
    },

    currentFlowSteps() {
      return this.currentFlowResult && Array.isArray(this.currentFlowResult.steps)
        ? this.currentFlowResult.steps
        : [];
    },

    currentFlowFailures() {
      if (!this.currentFlowSteps.length) {
        return [];
      }
      return this.currentFlowSteps.flatMap((step) => {
        if (step.error) {
          return [`${this.flowResultStepLabel(step)}：${step.error}`];
        }
        const failures = assertionFailureReasons(step.result && step.result.assertion);
        const prefix = this.flowResultStepLabel(step);
        return failures.map((reason) => `${prefix}：${reason}`);
      });
    },

    stressFlowLabel() {
      if (!this.stressTargetFlow) {
        return "未选择流程";
      }
      return `#${this.stressTargetFlow.id} ${this.stressTargetFlow.name || "未命名流程"}`;
    },

    stressTotalRuns() {
      return Number(this.stressForm.threads || 0) * Number(this.stressForm.rounds || 0);
    },

    stressMaxRounds() {
      const threads = Math.min(20, Math.max(1, Number(this.stressForm.threads || 1)));
      return Math.max(1, Math.floor(200 / threads));
    },

    stressRunning() {
      return Boolean(this.stressTask && this.stressTask.status === "running");
    },

    stressTaskCompleted() {
      return this.stressTask ? Number(this.stressTask.completed || 0) : 0;
    },

    stressTaskTotal() {
      return this.stressTask ? Number(this.stressTask.total || 0) : this.stressTotalRuns;
    },

    stressProgressPercent() {
      if (!this.stressTaskTotal) {
        return 0;
      }
      return Math.min(100, Math.round((this.stressTaskCompleted / this.stressTaskTotal) * 100));
    },

    stressProgressStatus() {
      if (!this.stressTask) {
        return "";
      }
      if (this.stressTask.status === "finished") {
        return "success";
      }
      if (this.stressTask.status === "failed") {
        return "exception";
      }
      return "";
    },

    stressTaskStatusText() {
      if (!this.stressTask) {
        return "";
      }
      if (this.stressTask.status === "finished") {
        return "压测完成";
      }
      if (this.stressTask.status === "failed") {
        return "压测失败";
      }
      return `压测中：${this.stressTask.options?.threads || this.stressForm.threads} 线程 × ${this.stressTask.options?.rounds || this.stressForm.rounds} 轮`;
    },
  },

  beforeUnmount() {
    this.clearStressPoll();
    if (this.stressRunning) {
      this.setRunningFlowStressId(null);
    }
  },

  methods: {
    ...mapActions([
      "selectFlow",
      "saveFlow",
      "deleteFlow",
      "bindFlowJob",
      "runFlow",
      "stressFlow",
      "fetchStressTask",
      "exportFlowReport",
      "fillJobSessionHeaders",
      "fillFlowSessionHeaders",
    ]),
    ...mapMutations({
      setFlowFormFieldMutation: "setFlowFormField",
      setFlowStepsMutation: "setFlowSteps",
      setRunningFlowStressId: "setRunningFlowStressId",
    }),
    flowChainLabel,
    flowJobOptionLabel,
    jobOptionLabel,
    methodTagType,

    updateFlowField(field, value) {
      this.setFlowFormFieldMutation({ field, value });
    },

    flowStepIds() {
      return (this.flowForm.steps || []).map((step) => String(step || "").trim()).filter(Boolean);
    },

    moveFlowStep(index, offset) {
      this.setFlowStepsMutation(moveFlowStepAt(this.flowForm.steps, index, offset));
    },

    removeFlowStep(index) {
      const removed = this.flowStepIds()[index];
      this.setFlowStepsMutation(removeFlowStepAt(this.flowForm.steps, index));
      if (this.flowForm.afterStep === removed) {
        this.updateFlowField("afterStep", "");
      }
    },

    handleFlowNodeDragStart(index, event) {
      this.draggedFlowIndex = index;
      if (event && event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(index));
      }
    },

    handleFlowNodeDrop(targetIndex) {
      const sourceIndex = this.draggedFlowIndex;
      this.setFlowStepsMutation(dropFlowStepAt(this.flowForm.steps, sourceIndex, targetIndex));
      this.draggedFlowIndex = null;
    },

    handleFlowNodeDragEnd() {
      this.draggedFlowIndex = null;
    },

    flowNodeLabel(job) {
      if (job && job.missing) {
        return job.step ? `Step ${job.step} 已不存在` : "Step 已不存在";
      }
      return flowJobOptionLabel(job);
    },

    flowNodeKey(job, index = 0) {
      return `${job && job.step ? job.step : "missing"}-${index}`;
    },

    isBindStepSelected(step) {
      const id = String(step || "").trim();
      return (this.flowForm.bindSteps || []).map((value) => String(value || "").trim()).includes(id);
    },

    toggleBindStep(step) {
      const id = String(step || "").trim();
      if (!id) {
        return;
      }
      const current = (this.flowForm.bindSteps || []).map((value) => String(value || "").trim());
      const next = current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id];
      this.updateFlowField("bindSteps", next);
    },

    updateBusinessBindSteps(values) {
      const businessSteps = new Set(this.businessJobs.map((job) => String(job.step || "").trim()));
      const current = (this.flowForm.bindSteps || []).map((value) => String(value || "").trim());
      const authSelections = current.filter((step) => !businessSteps.has(step));
      this.updateFlowField("bindSteps", [...authSelections, ...values.map((value) => String(value || "").trim())]);
    },

    flowResultStepLabel(stepResult) {
      const step = String(stepResult.step || (stepResult.job && stepResult.job.step) || "").trim();
      const description = stepResult.job && stepResult.job.step_desc;
      if (step && description) {
        return `Step ${step} ${description}`;
      }
      if (step) {
        return `Step ${step}`;
      }
      return description || "Step";
    },

    flowResultStepKey(stepResult, index = 0) {
      return `${stepResult.step || (stepResult.job && stepResult.job.step) || "step"}-${index}`;
    },

    flowResultUrl(stepResult) {
      return (stepResult.job && stepResult.job.url) || (stepResult.result && stepResult.result.url) || "-";
    },

    flowResultBlockFlow(stepResult) {
      return Boolean(stepResult.result && stepResult.result.assertion && stepResult.result.assertion.block_flow);
    },

    flowResultRequestText(stepResult) {
      return formatRequestParams(stepResult.result) || "暂无请求";
    },

    flowResultResponseText(stepResult) {
      return formatResponseBody(stepResult.result) || "暂无响应";
    },

    // The backend flattens response assertions and database rules into checks;
    // each check is rendered as one visible Rule row under the Step result.
    flowResultRules(stepResult) {
      const assertion = stepResult.result && stepResult.result.assertion;
      if (!assertion || assertion.skipped || !Array.isArray(assertion.checks)) {
        return [];
      }
      // block_flow is configured at the Step level, so every failed Rule in
      // the same Step has the same flow-interruption behavior.
      const blockFlow = Boolean(assertion.block_flow);
      return assertion.checks.map((check) => ({
        label: `${this.ruleSourceLabel(check)} ${check.rule || "-"}`,
        detail: this.ruleResultDetail(check),
        ok: Boolean(check.ok),
        blockFlow,
      }));
    },

    ruleSourceLabel(check) {
      return check && check.source === "database" ? "数据库断言" : "返回断言";
    },

    ruleResultDetail(check) {
      if (!check) {
        return "";
      }
      if (check.error) {
        return check.error;
      }
      return `实际 ${this.displayRuleValue(check.actual)}，期望 ${check.operator || "="} ${this.displayRuleValue(check.expected)}`;
    },

    displayRuleValue(value) {
      if (value === undefined) {
        return "undefined";
      }
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    },

    canFillSession(job) {
      return job && !job.missing && !isAuthJob(job);
    },

    async fillSessionForJob(job) {
      this.fillSessionJobId = Number(job.id);
      try {
        await this.fillJobSessionHeaders({
          jobId: job.id,
          loginJobId: this.loginJob && this.loginJob.id,
          verifyJobId: this.verifyCodeJob && this.verifyCodeJob.id,
          phone: this.flowSessionPhone,
        });
      } finally {
        this.fillSessionJobId = null;
      }
    },

    async fillSessionForFlow() {
      this.fillingFlowSession = true;
      try {
        await this.fillFlowSessionHeaders({
          flowId: this.flowForm.id,
          steps: this.flowForm.steps,
          jobIds: this.flowSessionTargetJobs.map((job) => job.id),
          loginJobId: this.loginJob && this.loginJob.id,
          verifyJobId: this.verifyCodeJob && this.verifyCodeJob.id,
          phone: this.flowSessionPhone,
        });
      } finally {
        this.fillingFlowSession = false;
      }
    },

    openFlowStressDialog(flow) {
      if (!flow || !flow.id) {
        this.$store.dispatch("showToast", { message: "请选择要压测的流程", isError: true });
        return;
      }
      this.stressTargetFlow = flow;
      this.stressResult = null;
      this.stressTask = null;
      this.stressForm.includeRules = true;
      this.stressDialogVisible = true;
    },

    async handleRunFlowStress() {
      if (!this.stressTargetFlow || !this.stressTargetFlow.id) {
        return;
      }
      this.normalizeStressForm();
      this.clearStressPoll();
      this.stressResult = null;
      this.stressTask = null;
      const task = await this.stressFlow({
        id: Number(this.stressTargetFlow.id),
        threads: this.stressForm.threads,
        rounds: this.stressForm.rounds,
        includeRules: this.stressForm.includeRules,
      });
      if (task) {
        await this.applyFlowStressTask(task);
      }
    },

    normalizeStressForm() {
      const threads = Math.min(20, Math.max(1, Number(this.stressForm.threads || 1)));
      const maxRounds = Math.max(1, Math.floor(200 / threads));
      const rounds = Math.min(maxRounds, Math.max(1, Number(this.stressForm.rounds || 1)));
      this.stressForm.threads = threads;
      this.stressForm.rounds = rounds;
    },

    clearStressPoll() {
      if (this.stressPollTimer) {
        window.clearTimeout(this.stressPollTimer);
        this.stressPollTimer = null;
      }
    },

    scheduleFlowStressPoll(taskId) {
      this.clearStressPoll();
      this.stressPollTimer = window.setTimeout(() => {
        this.pollFlowStressTask(taskId);
      }, 800);
    },

    async pollFlowStressTask(taskId) {
      const task = await this.fetchStressTask(taskId);
      if (!task) {
        this.clearStressPoll();
        this.setRunningFlowStressId(null);
        return;
      }
      await this.applyFlowStressTask(task);
    },

    async applyFlowStressTask(task) {
      this.stressTask = task;
      if (task.status === "running") {
        this.scheduleFlowStressPoll(task.task_id);
        return;
      }

      this.clearStressPoll();
      this.setRunningFlowStressId(null);
      if (task.status === "finished" && task.result) {
        this.stressResult = task.result;
        this.$store.dispatch("showToast", {
          message: `流程压测完成，成功率 ${task.result.success_rate}%`,
        });
        await this.$store.dispatch("loadJobs");
        await this.$store.dispatch("loadFlows");
        return;
      }

      this.$store.dispatch("showToast", {
        message: task.error || "流程压测失败",
        isError: true,
      });
    },

    formatStressMs(value) {
      return `${Number(value || 0).toFixed(2)} ms`;
    },

    async handleSaveFlow() {
      try {
        await this.$refs.flowEditor.validate();
      } catch (error) {
        return;
      }
      await this.saveFlow();
    },

    selectFlowByRow(row) {
      this.selectFlow(row.id);
    },

    flowRowClassName({ row }) {
      return this.selectedFlow && this.selectedFlow.id === row.id ? "selected-row" : "";
    },
  },
};
</script>
